import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, ClockCounterClockwise, PencilSimple, Trash, Receipt, Eye, Upload } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { uploadFile } from '../utils/uploadFile';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [hoursToAdd, setHoursToAdd] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    cep: '',
    address: '',
    client_type: 'comercial',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    plano_contratado: '',
    data_inicio_vigencia: '',
    data_fim_vigencia: '',
    dia_vencimento: 10,
    senha_inicial: ''
  });

  useEffect(() => {
    fetchClients();
    fetchPlanos();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchPlanos = async () => {
    try {
      const response = await api.get('/planos');
      setPlanos(response.data.filter(p => p.ativo !== false));
    } catch (error) {
      console.error('Error fetching planos:', error);
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            address: `${data.logradouro}, ${data.bairro} - ${data.localidade}/${data.uf}`
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const formatCNPJ = (value) => {
    const cnpj = value.replace(/\D/g, '');
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatCEP = (value) => {
    const cep = value.replace(/\D/g, '');
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/clients', formData);
      setIsDialogOpen(false);
      setCreatedCredentials({
        email: formData.contact_email,
        senha: response.data.senha_criada || formData.senha_inicial || 'Toolserv2026!',
        nome: formData.name
      });
      setIsCredentialsDialogOpen(true);
      setFormData({
        name: '',
        cnpj: '',
        inscricao_estadual: '',
        inscricao_municipal: '',
        cep: '',
        address: '',
        client_type: 'comercial',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        plano_contratado: '',
        data_inicio_vigencia: '',
        data_fim_vigencia: '',
        dia_vencimento: 10,
        senha_inicial: ''
      });
      fetchClients();
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Erro ao criar cliente: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddHours = async () => {
    if (!selectedClient || !hoursToAdd) return;
    
    try {
      await api.post('/clients/add-hours', {
        client_id: selectedClient.id,
        hours_to_add: parseFloat(hoursToAdd)
      });
      setIsHoursDialogOpen(false);
      setSelectedClient(null);
      setHoursToAdd('');
      fetchClients();
    } catch (error) {
      console.error('Error adding hours:', error);
      alert('Erro ao adicionar horas: ' + error.message);
    }
  };

  const openEditClient = (client) => {
    setSelectedClient(client);
    setEditData({
      name: client.name || '',
      cnpj: client.cnpj || '',
      inscricao_estadual: client.inscricao_estadual || '',
      inscricao_municipal: client.inscricao_municipal || '',
      cep: client.cep || '',
      address: client.address || '',
      client_type: client.client_type || 'comercial',
      contact_name: client.contact_name || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      plano_contratado: client.plano_contratado || '',
      data_inicio_vigencia: client.data_inicio_vigencia || '',
      data_fim_vigencia: client.data_fim_vigencia || '',
      dia_vencimento: client.dia_vencimento || 10
    });
    setIsEditOpen(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    try {
      await api.put(`/clients/${selectedClient.id}`, editData);
      setIsEditOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      alert('Erro ao editar cliente: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    try {
      await api.delete(`/clients/${selectedClient.id}`);
      setIsDeleteOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      alert('Erro ao excluir cliente: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Payment history
  const [isPaymentsOpen, setIsPaymentsOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ data: '', descricao: '', valor: '' });
  const [paymentFile, setPaymentFile] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);

  const openPayments = async (client) => {
    setSelectedClient(client);
    setIsPaymentsOpen(true);
    try {
      const res = await api.get(`/pagamentos-clientes?client_id=${client.id}`);
      setPayments(res.data);
    } catch (error) { console.error('Error fetching payments:', error); }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    setUploadingPayment(true);
    try {
      let comprovante_url = null;
      if (paymentFile) {
        comprovante_url = await uploadFile(paymentFile, `pagamentos/${selectedClient.id}`);
      }
      await api.post('/pagamentos-clientes', {
        client_id: selectedClient.id,
        data: paymentForm.data,
        descricao: paymentForm.descricao,
        valor: parseFloat(paymentForm.valor),
        comprovante_url
      });
      setPaymentForm({ data: '', descricao: '', valor: '' });
      setPaymentFile(null);
      const res = await api.get(`/pagamentos-clientes?client_id=${selectedClient.id}`);
      setPayments(res.data);
    } catch (error) {
      alert('Erro ao registrar pagamento: ' + (error.response?.data?.detail || error.message));
    } finally { setUploadingPayment(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Gerenciar Clientes
        </h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="add-client-button">
              <Plus size={20} className="mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Cadastrar Novo Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dados da Empresa */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Dados da Empresa</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome da Empresa *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>CNPJ *</Label>
                    <Input
                      value={formData.cnpj}
                      onChange={(e) => setFormData({...formData, cnpj: formatCNPJ(e.target.value)})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      required
                      data-testid="client-cnpj-input"
                    />
                  </div>

                  <div>
                    <Label>Tipo de Cliente *</Label>
                    <Select value={formData.client_type} onValueChange={(value) => setFormData({...formData, client_type: value})}>
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="client-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        <SelectItem value="residencial">Residencial</SelectItem>
                        <SelectItem value="comercial">Comercial</SelectItem>
                        <SelectItem value="industrial">Industrial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Inscrição Estadual</Label>
                    <Input
                      value={formData.inscricao_estadual}
                      onChange={(e) => setFormData({...formData, inscricao_estadual: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                    />
                  </div>

                  <div>
                    <Label>Inscrição Municipal</Label>
                    <Input
                      value={formData.inscricao_municipal}
                      onChange={(e) => setFormData({...formData, inscricao_municipal: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Endereço</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CEP *</Label>
                    <Input
                      value={formData.cep}
                      onChange={(e) => {
                        const cep = formatCEP(e.target.value);
                        setFormData({...formData, cep});
                        if (cep.replace(/\D/g, '').length === 8) {
                          buscarCEP(cep);
                        }
                      }}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="00000-000"
                      maxLength={9}
                      required
                      data-testid="client-cep-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label>Endereço Completo *</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="client-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Dados de Contato</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome do Contato *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="client-contact-name-input"
                    />
                  </div>
                  <div>
                    <Label>Telefone *</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="(00) 00000-0000"
                      required
                      data-testid="client-contact-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Acesso ao Sistema */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Acesso ao Sistema</h3>
                <p className="text-zinc-500 text-xs mb-3">Essas credenciais serao usadas pelo cliente para fazer login na plataforma.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email (Login) *</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="email@empresa.com.br"
                      required
                      data-testid="client-login-email-input"
                    />
                  </div>
                  <div>
                    <Label>Senha *</Label>
                    <Input
                      type="text"
                      value={formData.senha_inicial}
                      onChange={(e) => setFormData({...formData, senha_inicial: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="Minimo 6 caracteres"
                      required
                      minLength={6}
                      data-testid="client-login-password-input"
                    />
                  </div>
                </div>
              </div>

              {/* Plano */}
              <div>
                <h3 className="text-[#FFB800] font-semibold mb-3">Plano Contratado</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome do Plano *</Label>
                    <Select 
                      value={formData.plano_contratado} 
                      onValueChange={(v) => setFormData({...formData, plano_contratado: v})}
                      required
                    >
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="client-plano-select">
                        <SelectValue placeholder="Selecione um plano..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        {planos.map(plano => (
                          <SelectItem key={plano.id} value={plano.nome}>
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-[#FFB800]">{plano.nome}</span>
                              <span className="text-zinc-400 text-xs">
                                {plano.horas_eletricista}h elet. / {plano.horas_auxiliar}h aux.
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Início da Vigência *</Label>
                    <Input
                      type="date"
                      value={formData.data_inicio_vigencia}
                      onChange={(e) => setFormData({...formData, data_inicio_vigencia: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="client-data-inicio-input"
                    />
                  </div>

                  <div>
                    <Label>Fim da Vigência *</Label>
                    <Input
                      type="date"
                      value={formData.data_fim_vigencia}
                      onChange={(e) => setFormData({...formData, data_fim_vigencia: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="client-data-fim-input"
                    />
                  </div>

                  <div>
                    <Label>Dia de Vencimento *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.dia_vencimento}
                      onChange={(e) => setFormData({...formData, dia_vencimento: parseInt(e.target.value)})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600] py-3" data-testid="submit-client-button">
                Cadastrar Cliente
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients Table */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm overflow-x-auto">
        <table className="w-full text-sm text-left text-zinc-400">
          <thead className="text-xs uppercase bg-[#050505] text-zinc-400">
            <tr>
              <th className="px-6 py-3">Nome</th>
              <th className="px-6 py-3">CNPJ</th>
              <th className="px-6 py-3">Plano</th>
              <th className="px-6 py-3">Vigência</th>
              <th className="px-6 py-3">Horas Contratadas</th>
              <th className="px-6 py-3">Horas Usadas</th>
              <th className="px-6 py-3">Disponíveis</th>
              <th className="px-6 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => {
              const available = client.contracted_hours - client.used_hours;
              return (
                <tr key={client.id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]" data-testid={`client-row-${client.cnpj}`}>
                  <td className="px-6 py-4 text-white font-medium">{client.name}</td>
                  <td className="px-6 py-4">{client.cnpj}</td>
                  <td className="px-6 py-4">{client.plano_contratado || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      {client.data_inicio_vigencia ? new Date(client.data_inicio_vigencia + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}
                      {' - '}
                      {client.data_fim_vigencia ? new Date(client.data_fim_vigencia + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">{client.contracted_hours.toFixed(1)}</td>
                  <td className="px-6 py-4">{client.used_hours.toFixed(1)}</td>
                  <td className={`px-6 py-4 font-bold ${
                    available < 5 ? 'text-[#FFB800]' : 'text-[#22C55E]'
                  }`}>
                    {available.toFixed(1)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openPayments(client)}
                        className="text-[#22C55E] hover:text-[#16A34A] flex items-center gap-1 text-xs"
                        data-testid={`payments-${client.cnpj}`}
                      >
                        <Receipt size={14} />
                        Pagtos
                      </button>
                      <button
                        onClick={() => { setSelectedClient(client); setIsHoursDialogOpen(true); }}
                        className="text-[#FFB800] hover:text-[#E6A600] flex items-center gap-1 text-xs"
                        data-testid={`add-hours-${client.cnpj}`}
                      >
                        <ClockCounterClockwise size={14} />
                        Horas
                      </button>
                      <button
                        onClick={() => openEditClient(client)}
                        className="text-zinc-400 hover:text-[#FFB800] transition-colors"
                        data-testid={`edit-client-${client.cnpj}`}
                      >
                        <PencilSimple size={14} />
                      </button>
                      <button
                        onClick={() => { setSelectedClient(client); setIsDeleteOpen(true); }}
                        className="text-zinc-400 hover:text-red-500 transition-colors"
                        data-testid={`delete-client-${client.cnpj}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Hours Dialog */}
      <Dialog open={isHoursDialogOpen} onOpenChange={setIsHoursDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Adicionar Horas</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4">
              <div>
                <div className="text-zinc-400 text-sm">Cliente</div>
                <div className="text-white font-medium">{selectedClient.name}</div>
              </div>
              <div>
                <div className="text-zinc-400 text-sm">Horas Atuais</div>
                <div className="text-[#FFB800] text-2xl font-bold">{selectedClient.contracted_hours.toFixed(1)}</div>
              </div>
              <div>
                <Label>Horas para Adicionar</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={hoursToAdd}
                  onChange={(e) => setHoursToAdd(e.target.value)}
                  className="bg-[#050505] border-[#27272A] text-white"
                  data-testid="hours-input"
                />
              </div>
              <Button 
                onClick={handleAddHours}
                className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]"
                data-testid="confirm-add-hours"
              >
                Confirmar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={isCredentialsDialogOpen} onOpenChange={setIsCredentialsDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#22C55E]">Cliente Criado com Sucesso!</DialogTitle>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">Anote as credenciais de acesso do cliente:</p>
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm space-y-3">
                <div>
                  <div className="text-zinc-400 text-xs">Nome</div>
                  <div className="text-white font-medium">{createdCredentials.nome}</div>
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">Email de Acesso</div>
                  <div className="text-[#FFB800] font-mono" data-testid="created-email">{createdCredentials.email}</div>
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">Senha</div>
                  <div className="text-[#FFB800] font-mono" data-testid="created-password">{createdCredentials.senha}</div>
                </div>
              </div>
              <p className="text-zinc-500 text-xs">Compartilhe essas credenciais com o cliente para que ele possa acessar o sistema.</p>
              <Button onClick={() => setIsCredentialsDialogOpen(false)} className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]">
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Editar Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditClient} className="space-y-6">
            {/* Dados da Empresa */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Dados da Empresa</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome da Empresa *</Label>
                  <Input value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="edit-client-name" />
                </div>
                <div>
                  <Label>CNPJ *</Label>
                  <Input value={editData.cnpj || ''} onChange={(e) => setEditData({...editData, cnpj: formatCNPJ(e.target.value)})} className="bg-[#050505] border-[#27272A] text-white" placeholder="00.000.000/0000-00" maxLength={18} required />
                </div>
                <div>
                  <Label>Tipo de Cliente *</Label>
                  <Select value={editData.client_type || 'comercial'} onValueChange={(v) => setEditData({...editData, client_type: v})}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                      <SelectItem value="residencial">Residencial</SelectItem>
                      <SelectItem value="comercial">Comercial</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Inscricao Estadual</Label>
                  <Input value={editData.inscricao_estadual || ''} onChange={(e) => setEditData({...editData, inscricao_estadual: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
                <div>
                  <Label>Inscricao Municipal</Label>
                  <Input value={editData.inscricao_municipal || ''} onChange={(e) => setEditData({...editData, inscricao_municipal: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
              </div>
            </div>

            {/* Endereco */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Endereco</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CEP *</Label>
                  <Input value={editData.cep || ''} onChange={(e) => { const cep = formatCEP(e.target.value); setEditData({...editData, cep}); }} className="bg-[#050505] border-[#27272A] text-white" placeholder="00000-000" maxLength={9} required />
                </div>
                <div className="col-span-2">
                  <Label>Endereco Completo *</Label>
                  <Input value={editData.address || ''} onChange={(e) => setEditData({...editData, address: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
              </div>
            </div>

            {/* Contato */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Dados de Contato</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Contato *</Label>
                  <Input value={editData.contact_name || ''} onChange={(e) => setEditData({...editData, contact_name: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>Telefone *</Label>
                  <Input value={editData.contact_phone || ''} onChange={(e) => setEditData({...editData, contact_phone: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="(00) 00000-0000" required />
                </div>
                <div className="col-span-2">
                  <Label>Email de Contato</Label>
                  <Input type="email" value={editData.contact_email || ''} className="bg-[#050505] border-[#27272A] text-zinc-500" disabled />
                  <p className="text-zinc-500 text-xs mt-1">O email de login nao pode ser alterado.</p>
                </div>
              </div>
            </div>

            {/* Plano */}
            <div>
              <h3 className="text-[#FFB800] font-semibold mb-3">Plano Contratado</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome do Plano *</Label>
                  <Select value={editData.plano_contratado || '_none'} onValueChange={(v) => setEditData({...editData, plano_contratado: v === '_none' ? '' : v})}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue placeholder="Selecione um plano..." /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                      <SelectItem value="_none">Nenhum</SelectItem>
                      {planos.map(plano => (
                        <SelectItem key={plano.id} value={plano.nome}>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-[#FFB800]">{plano.nome}</span>
                            <span className="text-zinc-400 text-xs">{plano.horas_eletricista}h elet. / {plano.horas_auxiliar}h aux.</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Inicio da Vigencia</Label>
                  <Input type="date" value={editData.data_inicio_vigencia || ''} onChange={(e) => setEditData({...editData, data_inicio_vigencia: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
                <div>
                  <Label>Fim da Vigencia</Label>
                  <Input type="date" value={editData.data_fim_vigencia || ''} onChange={(e) => setEditData({...editData, data_fim_vigencia: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
                <div>
                  <Label>Dia de Vencimento</Label>
                  <Input type="number" min="1" max="31" value={editData.dia_vencimento || ''} onChange={(e) => setEditData({...editData, dia_vencimento: parseInt(e.target.value)})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600] py-3" data-testid="submit-edit-client">
              Salvar Alteracoes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Client Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Excluir Cliente</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4">
              <p className="text-zinc-400">Tem certeza que deseja excluir o cliente <strong className="text-white">{selectedClient.name}</strong>?</p>
              <p className="text-red-400 text-xs">Esta acao nao pode ser desfeita.</p>
              <div className="flex gap-3">
                <Button onClick={() => setIsDeleteOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]">Cancelar</Button>
                <Button onClick={handleDeleteClient} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-client">Excluir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payments History Dialog */}
      <Dialog open={isPaymentsOpen} onOpenChange={setIsPaymentsOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Historico de Pagamentos</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-sm text-zinc-400">Cliente</div>
                <div className="text-lg font-bold text-[#FFB800]">{selectedClient.name}</div>
                <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                  <div>
                    <div className="text-zinc-500 text-xs">Plano</div>
                    <div className="text-white">{selectedClient.plano_contratado || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Vigencia</div>
                    <div className="text-white">
                      {selectedClient.data_inicio_vigencia ? new Date(selectedClient.data_inicio_vigencia + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}
                      {' - '}
                      {selectedClient.data_fim_vigencia ? new Date(selectedClient.data_fim_vigencia + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Vencimento</div>
                    <div className="text-white">Dia {selectedClient.dia_vencimento || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Add Payment Form */}
              <form onSubmit={handleAddPayment} className="p-4 bg-[#050505] border border-[#27272A] rounded-sm space-y-3">
                <h4 className="text-[#FFB800] font-semibold text-sm">Registrar Pagamento</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data *</Label>
                    <Input type="date" value={paymentForm.data} onChange={(e) => setPaymentForm({...paymentForm, data: e.target.value})} className="bg-[#121212] border-[#27272A] text-white text-sm" required data-testid="payment-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Valor (R$) *</Label>
                    <Input type="number" step="0.01" value={paymentForm.valor} onChange={(e) => setPaymentForm({...paymentForm, valor: e.target.value})} className="bg-[#121212] border-[#27272A] text-white text-sm" placeholder="0.00" required data-testid="payment-valor-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Comprovante</Label>
                    <Input type="file" accept="image/*,.pdf" onChange={(e) => setPaymentFile(e.target.files[0])} className="bg-[#121212] border-[#27272A] text-white text-sm" data-testid="payment-comprovante-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Descricao *</Label>
                  <Input value={paymentForm.descricao} onChange={(e) => setPaymentForm({...paymentForm, descricao: e.target.value})} className="bg-[#121212] border-[#27272A] text-white text-sm" placeholder="Ex: Pagamento mensal plano START" required data-testid="payment-descricao-input" />
                </div>
                <Button type="submit" className="bg-[#FFB800] text-black hover:bg-[#E6A600] text-sm" disabled={uploadingPayment} data-testid="submit-payment-button">
                  {uploadingPayment ? 'Enviando...' : 'Registrar Pagamento'}
                </Button>
              </form>

              {/* Payment List */}
              <div>
                <h4 className="text-white font-semibold text-sm mb-3">Pagamentos Registrados</h4>
                {payments.length === 0 ? (
                  <div className="text-zinc-500 text-center py-4 text-sm">Nenhum pagamento registrado</div>
                ) : (
                  <div className="space-y-2">
                    {payments.map(p => (
                      <div key={p.id} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm flex items-center justify-between" data-testid={`payment-row-${p.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-white font-medium text-sm">{p.descricao}</span>
                            <span className="text-[#22C55E] font-bold text-sm">R$ {p.valor?.toFixed(2)}</span>
                          </div>
                          <div className="text-zinc-500 text-xs mt-1">
                            {new Date(p.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                            {p.created_by && <span className="ml-2">por {p.created_by}</span>}
                          </div>
                        </div>
                        {p.comprovante_url && (
                          <a href={p.comprovante_url} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:text-[#E6A600] text-xs flex items-center gap-1">
                            <Eye size={14} />
                            Ver
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientManagement;
